import { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import type { DriveStep } from 'driver.js';
import { useLocation, useNavigate } from 'react-router-dom';
import { TUTORIALS, type TutorialPageConfig } from '../../const';
import { useStoreUser } from '../../context';
import { useTutorial, useIsSmallScreen } from '../../hooks';
import { i18n } from '../../i18n';
import { Typography } from '../typography';
import { Button } from '../button';
import { ModalBase, ModalWrapper } from './ModalComponents';

export const tutorialsModal: { open: () => void } = { open: () => null };

const Sections = styled.div`
  display: grid;
  grid-auto-rows: max-content;
  grid-row-gap: var(--spacing-6);
`;

const PageBlockName = styled(Typography)`
  position: absolute;
  left: var(--spacing-2);
  top: 0;
  transform: translateY(-50%);
  background-color: var(--gray-30);
  border-radius: var(--border-radius-8);
  padding: 0 var(--spacing-2);
  transition: color 0.3s;
`;

const PageBlock = styled.div`
  display: grid;
  grid-auto-rows: max-content;
  grid-row-gap: var(--spacing-4);
  padding: var(--spacing-4);
  background-color: var(--gray-30);
  border: 0.1rem solid var(--gray-50);
  border-radius: var(--border-radius-8);
  box-shadow: 0 0.1rem 0.3rem rgba(19, 21, 35, 0.04);
  position: relative;
  transition: border-color 0.3s, box-shadow 0.3s;

  &:hover {
    border-color: var(--primary-50);
    box-shadow: 0 0.2rem 0.8rem rgba(30, 94, 255, 0.08);

    ${PageBlockName} {
      color: var(--primary-100);
    }
  }
`;

const Section = styled.div`
  display: grid;
  grid-auto-rows: max-content;
  grid-row-gap: var(--spacing-4);
`;

const TourButtons = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: var(--spacing-3);
`;

const getTourSteps = (page: TutorialPageConfig, tourKey: string): DriveStep[] => {
  const steps = page[tourKey];
  return Array.isArray(steps) ? (steps as DriveStep[]) : [];
};

const openDrawer = (steps: DriveStep[]) => {
  if (
    steps.some(
      (step) => typeof step.element === 'string' && step.element.includes('#drawer-button'),
    )
  ) {
    document
      .querySelector('#appbar-menu-icon')
      ?.dispatchEvent(new MouseEvent('click', { bubbles: true, cancelable: true }));

    return true;
  }
};

const TutorialPageBlock = ({
  page,
  onTourClick,
}: {
  page: TutorialPageConfig;
  onTourClick: (page: TutorialPageConfig, tourKey: string) => void;
}) => {
  return (
    <PageBlock>
      <PageBlockName variant="textM" weight="medium" color="--gray-100">
        {page.NAME}
      </PageBlockName>
      <TourButtons>
        {page.BUTTON_TOURS.map((tour) => (
          <Button
            key={`${page.ROUTE}-${tour.key}`}
            size="medium"
            onClick={() => onTourClick(page, tour.key)}
          >
            {tour.name}
          </Button>
        ))}
      </TourButtons>
    </PageBlock>
  );
};

export const TutorialsModal = () => {
  const [open, setOpen] = useState(false);
  const { user } = useStoreUser();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const isSmallScreen = useIsSmallScreen();
  const { startTour } = useTutorial();

  const tutorialPages = useMemo(
    () => Object.values(TUTORIALS(user, isSmallScreen)) as TutorialPageConfig[],
    [user],
  );

  useEffect(() => {
    tutorialsModal.open = () => setOpen(true);
  }, []);

  const currentPageTutorial = useMemo(
    () =>
      tutorialPages.find(
        (page) =>
          !page.DISABLED_FIND_ROUTE &&
          (pathname === page.ROUTE ||
            (Array.isArray(page.ROUTE) && page.ROUTE.some((route) => pathname === route))),
      ),
    [pathname, tutorialPages],
  );

  const handleOnClose = () => setOpen(false);

  const handleTourClick = useCallback(
    (page: TutorialPageConfig, tourKey: string) => {
      let navigateTo = null;
      const steps = getTourSteps(page, tourKey);
      if (!steps.length) return;

      setOpen(false);

      const runTour = () => {
        const isDrawerOpened = openDrawer(steps);
        setTimeout(() => startTour(steps), isDrawerOpened ? 400 : 0);
      };

      if (Array.isArray(page.ROUTE)) {
        const isRouteFound = page.ROUTE.some((route) => {
          return pathname === route;
        });

        if (!isRouteFound) navigateTo = page.ROUTE[0];
      } else if (pathname !== page.ROUTE) {
        navigateTo = page.ROUTE;
      }

      if (navigateTo) {
        navigate(navigateTo);
        window.setTimeout(runTour, 400);
        return;
      }

      runTour();
    },
    [navigate, pathname, startTour],
  );

  return (
    <ModalBase
      id="tutorials-modal"
      paperSize={isSmallScreen ? 'mobile' : 'default'}
      open={open}
      onClose={handleOnClose}
    >
      <ModalWrapper>
        <Typography variant="textL" weight="bold">
          {i18n.t('tutorial.title')}
        </Typography>

        <Sections>
          {currentPageTutorial && (
            <Section>
              <Typography variant="textM" weight="bold" color="--gray-100">
                {i18n.t('tutorial.currentTutorial')}
              </Typography>
              <TutorialPageBlock page={currentPageTutorial} onTourClick={handleTourClick} />
            </Section>
          )}

          <Section>
            <Typography variant="textM" weight="bold" color="--gray-100">
              {i18n.t('tutorial.allTutorials')}
            </Typography>
            {tutorialPages.map((page) => (
              <TutorialPageBlock key={Math.random()} page={page} onTourClick={handleTourClick} />
            ))}
          </Section>
        </Sections>
      </ModalWrapper>
    </ModalBase>
  );
};
